import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { collectorDisplayName } from "../map/collectorIdentity";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Container,
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Button,
  Chip,
  Avatar,
  Stack,
  Divider,
  Alert,
  AlertTitle,
} from "@mui/material";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import { useUser } from "../users/providers/UserProvider";
import { useTradeActions } from "./providers/TradeActionProvider";
import { useSnack } from "../providers/SnackBarProvider";
import { useCollection } from "../cards/collectionProvider/CollectionProvider";
import { useWishlist } from "../wishlist/providers/WishlistProvider";
import { useNotifications } from "../notifications/providers/NotificationProvider";
import ROUTES from "../router/routesModel";
import {
  getMyTradeRequests,
  declineTradeRequest,
  cancelTradeRequest,
  abandonTradeRequest,
  disputeTradeRequest,
  confirmTradeCompletion,
  isStaleTradeError,
} from "./tradeApi";
import {
  idOf,
  bucketTrades,
  availableActions,
  awaitsMyMeetupAnswer,
  completionPhase,
  interpretCompleteResult,
  tradeCardImages,
  tradesWithUnreadUpdate,
} from "./tradeUiLogic";
import TradeCompletionMoment from "./TradeCompletionMoment";
import TradeInboxCard from "./TradeInboxCard";
import { nextActionFor } from "./tradeJourneyLogic";
import { MOTION } from "../styles/motionTokens";
import Loader from "../styles/Loader";
import { trackEvent } from "../analytics/analytics";
import { claimRefusals } from "../utils/refusalNotice";

const STATUS_CHIP = {
  pending: { label: "Pending", color: "warning" },
  accepted: { label: "In progress", color: "info" },
  completed: { label: "Completed", color: "success" },
  declined: { label: "Declined", color: "default" },
  cancelled: { label: "Cancelled", color: "default" },
  expired: { label: "Expired", color: "default" },
  disputed: { label: "On hold", color: "error" },
  abandoned: { label: "Not completed", color: "default" },
};

/**
 * Safety guidance, collapsed.
 *
 * A full-width alert sat on top of every tab and was scrolled past within a day.
 * It becomes one quiet row that expands in place — NOTHING is deleted, and the
 * full guidance also belongs at the meetup checkpoint of the journey, where it
 * is actually actionable rather than ambient.
 */
const SafetyChecklist = () => {
  const [open, setOpen] = useState(false);
  return (
    <Box data-testid="trade-safety-checklist" sx={{ mb: 2 }}>
      <Box
        component="button"
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        data-testid="trade-safety-toggle"
        sx={{
          display: "flex", alignItems: "center", gap: 1, width: "100%",
          px: 1.5, py: 1, borderRadius: 2, cursor: "pointer", textAlign: "left",
          border: "1px solid", borderColor: "divider", background: "#f8fafc",
          font: "inherit", color: "#475569",
        }}
      >
        <ShieldOutlinedIcon fontSize="small" />
        <Typography sx={{ flex: 1, fontSize: 12.5, fontWeight: 700 }}>
          Public place, daylight, inspect before you hand over.
        </Typography>
        <Typography sx={{ fontSize: 11, fontWeight: 800, color: "#6d28d9" }}>
          {open ? "Less" : "More"}
        </Typography>
      </Box>
      {open && (
        <Typography
          data-testid="trade-safety-detail"
          sx={{ px: 1.5, pt: 1, fontSize: 12.5, color: "#475569" }}
        >
          Meet in a <b>public place in daylight</b>, bring a friend if you can, and
          <b> inspect the cards before you hand yours over</b>. Never share your home
          address — keep the conversation in the app so there&apos;s a record.
        </Typography>
      )}
    </Box>
  );
};

const CardThumbs = ({ items }) => (
  <Stack direction="row" spacing={0.5} sx={{ flexWrap: "wrap", gap: 0.5 }}>
    {(items || []).map((it, i) => {
      const c = it.cardId || {};
      return (
        <img
          key={c._id || i}
          src={c.images?.small || "https://placehold.co/44x60?text=?"}
          alt={c.name || "Card"}
          title={c.name || ""}
          loading="lazy"
          style={{ width: 44, height: 60, borderRadius: 4, objectFit: "cover" }}
        />
      );
    })}
    {(!items || items.length === 0) && (
      <Typography variant="caption" color="text.secondary">
        (nothing)
      </Typography>
    )}
  </Stack>
);

const TradeCard = ({ trade, myId, onAction, busyId }) => {
  const navigate = useNavigate();
  const iAmSender = idOf(trade.senderId) === String(myId);
  const other = iAmSender ? trade.receiverId : trade.senderId;
  const chip = STATUS_CHIP[trade.status] || { label: trade.status, color: "default" };
  const busy = busyId === trade._id;
  const actions = availableActions(trade, myId);
  const phase = completionPhase(trade, myId);

  return (
    <Paper
      variant="outlined"
      sx={{ p: 2, mb: 1.5, borderRadius: 2 }}
      data-testid={`trade-card-${trade._id}`}
      data-trade-status={trade.status}
      data-completion-phase={phase || ""}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
        <Avatar
          src={other?.image?.url}
          sx={{ width: 36, height: 36, cursor: "pointer" }}
          onClick={() => other?._id && navigate(`${ROUTES.PROFILE}/${other._id}`)}
        >
          {Array.from(other?.name?.first || "")[0] || "?"}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" noWrap>
            {iAmSender ? "To " : "From "}
            {/* A name keeps its own direction inside the line (B-05). */}
            <bdi>{collectorDisplayName(other)}</bdi>
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {new Date(trade.createdAt).toLocaleDateString()}
          </Typography>
        </Box>
        <Chip size="small" label={chip.label} color={chip.color} />
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, my: 1 }}>
        <Box>
          <Typography variant="caption" color="text.secondary">
            {iAmSender ? "You give" : "You get"}
          </Typography>
          <CardThumbs items={trade.offeredCards} />
        </Box>
        <SwapHorizIcon color="action" />
        <Box>
          <Typography variant="caption" color="text.secondary">
            {iAmSender ? "You get" : "You give"}
          </Typography>
          <CardThumbs items={trade.requestedCards} />
        </Box>
      </Box>

      {trade.message && (
        // B-05: the note's own direction, so Hebrew reads right to left.
        <Typography dir="auto" variant="body2" color="text.secondary" sx={{ fontStyle: "italic", mb: 1 }}>
          “{trade.message}”
        </Typography>
      )}

      <Divider sx={{ my: 1 }} />

      <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center">
        {/* Q-07: an offer is answered on its own page, so this row opens it —
            Decline with the trade page's confirm over it (B-66). */}
        {actions.includes("decline") && (
          <Button
            size="small"
            color="inherit"
            disabled={busy}
            onClick={() => navigate(`${ROUTES.TRADES}/${trade._id}`, { state: { decline: true } })}
          >
            Decline
          </Button>
        )}
        {actions.includes("accept") && (
          <Button
            size="small"
            variant="contained"
            disabled={busy}
            onClick={() => navigate(`${ROUTES.TRADES}/${trade._id}`)}
            sx={{ transition: `transform ${MOTION.pressMs}ms ease` }}
          >
            Review offer
          </Button>
        )}
        {actions.includes("cancel") && (
          <Button size="small" color="inherit" disabled={busy} onClick={() => onAction("cancel", trade)}>
            Cancel request
          </Button>
        )}
        {phase === "waiting" && (
          <Chip
            size="small"
            variant="outlined"
            color="success"
            label="You confirmed — waiting for the other trader"
            data-testid="trade-waiting-chip"
          />
        )}
        {actions.includes("complete") && (
          <Button
            size="small"
            variant="contained"
            color="success"
            disabled={busy}
            data-testid="trade-confirm-swap"
            onClick={() => onAction("complete", trade)}
            sx={{ transition: `transform ${MOTION.pressMs}ms ease` }}
          >
            {busy ? <Loader context="inline" inheritColor label="Confirming" /> : "Confirm swap done"}
          </Button>
        )}
        {actions.includes("abandon") && (
          <Button
            size="small"
            color="inherit"
            disabled={busy}
            data-testid="trade-abandon"
            onClick={() => onAction("abandon", trade)}
          >
            Didn&apos;t happen
          </Button>
        )}
        {actions.includes("dispute") && (
          <Button
            size="small"
            color="error"
            disabled={busy}
            data-testid="trade-dispute"
            onClick={() => onAction("dispute", trade)}
          >
            Report a problem
          </Button>
        )}
        {trade.status === "completed" && trade.completedAt && (
          <Typography variant="caption" color="text.secondary">
            Completed {new Date(trade.completedAt).toLocaleDateString()}
          </Typography>
        )}
      </Stack>
    </Paper>
  );
};

/**
 * The journey model names the user's NEXT STEP; this table says what the inbox's
 * primary button does with it. Every key `nextActionFor` can return WITH A LABEL
 * must appear here, and there is deliberately no fallback: an unmapped key used
 * to fall through to `"complete"`, so a button reading "Arrange meetup" posted
 * the completion confirmation — on every accepted trade, since nothing writes
 * `meetup.agreedAt`.
 *
 * `mutate` names a trade API call; `to` only routes. `arrange`/`checkin` still
 * have no mutation — they land with the journey route and presence service — so
 * they open the journey detail, which is where those steps actually happen.
 */
const PRIMARY_INTENT = {
  // Owner decision Q-07: an offer is accepted only on its own page, with the
  // whole offer on screen, so "Review offer" opens it and never accepts here.
  respond: { to: (trade) => `${ROUTES.TRADES}/${trade._id}` },
  cancel: { mutate: "cancel" },
  confirm: { mutate: "complete" },
  arrange: { to: (trade) => `${ROUTES.TRADES}/${trade._id}` },
  checkin: { to: (trade) => `${ROUTES.TRADES}/${trade._id}` },
  // B-12: an offer whose card is promised elsewhere. Its Accept would be
  // refused, so the button opens the trade, which says why.
  promised: { to: (trade) => `${ROUTES.TRADES}/${trade._id}` },
  // B-07: across a block, the trade page explains and offers the way out.
  blocked: { to: (trade) => `${ROUTES.TRADES}/${trade._id}` },
  blocked_offer: { to: (trade) => `${ROUTES.TRADES}/${trade._id}` },
  review: { to: () => ROUTES.COLLECTION },
};

/**
 * Truthful labels for the buttons that still act INLINE. A step that opens the
 * trade keeps the journey model's own label ("Review offer", Q-07), which says
 * exactly that.
 */
const PRIMARY_LABEL = {
  cancel: "Cancel offer",
  confirm: "Confirm exchange",
};

/**
 * The four inbox tabs, as a pure function of the list.
 *
 * Lifted out of the component so `handleAction` can ask "is this row still
 * where the user is looking?" the moment a refetch resolves, without waiting
 * for a render. A refused action that moves its row to another tab has to say
 * so out loud, or the card — and the message explaining the refusal — simply
 * vanishes from under the tap (P2-55).
 */
export function inboxTabs(trades, myId) {
  const buckets = bucketTrades(trades, myId);

  // "Needs you" is EXACTLY pendingActionCount's definition — incoming pending
  // offers plus accepted trades the other collector has already confirmed —
  // reused rather than forked, so the tab and the nav badge can never disagree.
  const waitingOnMe = buckets.inProgress.filter((t) => {
    // A meetup suggestion only I can answer waits on me too (T-71) — same
    // predicate as pendingActionTradeIds, so the tab and the badge still agree.
    if (awaitsMyMeetupAnswer(t, myId)) return true;
    if (completionPhase(t, myId) !== "confirm") return false;
    const iAmSender = idOf(t.senderId) === String(myId);
    return iAmSender ? !!t.receiverCompleted : !!t.senderCompleted;
  });
  const needsYou = [...buckets.toRespond, ...waitingOnMe];

  // Action-needed first, so the loudest cards are also the topmost.
  const needIds = new Set(needsYou.map((t) => t._id));
  const active = [...buckets.inProgress].sort(
    (a, b) => (needIds.has(b._id) ? 1 : 0) - (needIds.has(a._id) ? 1 : 0)
  );

  return [
    { label: "Needs you", items: needsYou },
    { label: "Active", items: active },
    { label: "Sent", items: buckets.sent },
    { label: "History", items: buckets.history },
  ];
}

// Indexes into `inboxTabs`: the current-trade tabs in the order they are
// preferred, then History.
const CURRENT_TAB_PRIORITY = [0, 1, 2]; // Needs you > Active > Sent
const HISTORY_TAB = 3;

/**
 * The tab the collector picked on this visit, kept on the inbox's own history
 * entry (E2E audit 23·3): Back from a trade opened from History used to land
 * on Needs you. Null when they picked nothing, so a fresh visit still opens by
 * the rule below.
 */
const rememberedTab = (state) => {
  const t = state?.tradesTab;
  return Number.isInteger(t) && t >= 0 && t <= HISTORY_TAB ? t : null;
};

/**
 * The tab the Trades screen opens on (owner decision 2026-09-27): the first
 * current tab with trades in it, so nobody lands on an empty tab while their
 * trade activity sits one tap away. History only when there are no current
 * trades at all — past trades never outrank live ones. With nothing anywhere
 * it stays on Needs you, the normal empty Trades state.
 */
/** What an empty tab says when the collector does have trades elsewhere. */
const EMPTY_TAB_TITLE = [
  "Nothing needs you right now",
  "No trades in progress",
  "No offers waiting on the other side",
  "No finished trades yet",
];

/**
 * The tab an empty tab should point to: the first other tab with trades, in
 * the same order the screen opens on (Needs you > Active > Sent > History).
 * Null when every other tab is empty too — then "No trades yet" is the truth.
 */
export function otherTabWithTrades(tabs, current) {
  const i = [0, 1, 2, 3].find((k) => k !== current && tabs[k]?.items.length > 0);
  return i === undefined ? null : i;
}

export function initialTabIndex(tabs) {
  const firstCurrent = CURRENT_TAB_PRIORITY.find((i) => tabs[i]?.items.length > 0);
  if (firstCurrent !== undefined) return firstCurrent;
  if (tabs[HISTORY_TAB]?.items.length > 0) return HISTORY_TAB;
  return 0;
}

// Each trade as both the badge poll (GET /trades/summary) and the list
// (GET /trades) carry it, so the page can tell whether a poll answer shows
// anything the list on screen does not. updatedAt moves on every write to a
// trade; status is named too because the server's expiry pass rewrites it in
// an answer without touching updatedAt there. Sorted, because both reads sort
// by createdAt and two trades created in the same millisecond may come back in
// either order.
const pollView = (list) =>
  list.map((t) => t._id + t.status + !!t.senderCompleted + !!t.receiverCompleted + t.updatedAt).sort().join();

const TradesPage = () => {
  const { user } = useUser();
  // `tradesSig` and `syncedAt` come from the app-wide 30-second badge poll;
  // see the effect after `load` for what this page does with them.
  const { refresh: refreshTradeActions, tradesSig, syncedAt } = useTradeActions();
  const { notifications, unreadTradeIds, refresh: refreshNotifications } = useNotifications();
  const setSnack = useSnack();
  const navigate = useNavigate();
  // The Vault is loaded per sign-in (CollectionProvider.jsx:124-135, keyed on
  // `[user?._id, loadCollection]`), and otherwise only on app return or when
  // the badge poll sees a trade complete (B-56). A completed trade reassigns
  // cards on the server (tradeTransferService.js:99-100), so without a refetch
  // the cached collection keeps the card we gave away and lacks the one we
  // received. The journey page confirms the same trade through its own handler
  // and needed the same call (TradeJourneyPage.jsx:209); this is the inbox's
  // copy of it.
  const { refreshCollection } = useCollection();
  // Same story for the hearts. The server deletes the receiver's wishlist rows
  // for every card it just handed over (`clearWishlistEntries`,
  // tradeTransferService.js:69-72, called at :296-297), while the client's id
  // Set is filled per sign-in (WishlistProvider.jsx:84-92) — so a card you
  // just received keeps a filled heart against a row that no longer exists.
  const { refreshWishlistIds } = useWishlist();
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  // Set when `load()` fails, cleared when it succeeds. Without it a failed
  // fetch left `trades` at [] and the page rendered the "Nothing here yet."
  // card, so an empty inbox and a broken one looked identical once the
  // snackbar auto-hid (SnackBarProvider.jsx `autoHideDuration={3000}`).
  const [loadError, setLoadError] = useState(false);
  // True once a load has landed. Until then the tab counts are unknown, and
  // "Needs you (0) · Active (0)" read as real zeros before jumping (B-01).
  const [loaded, setLoaded] = useState(false);
  const location = useLocation();
  const [tab, setTab] = useState(() => rememberedTab(location.state) ?? 0);
  // The opening tab is chosen ONCE per visit, when the first successful load
  // lands (see the effect after `tabs`). After that only the user moves it: a
  // refetch that empties or fills a tab must never pull them somewhere else.
  // A tap before the first load lands counts as that choice.
  // A tab remembered on this history entry (Back from a trade) is that choice.
  const tabDecided = useRef(rememberedTab(location.state) !== null);
  // The collector's pick: shown now, and written onto this history entry so
  // Back from a trade they open lands on it again (23·3).
  const chooseTab = (v) => {
    tabDecided.current = true;
    setTab(v);
    navigate(`${location.pathname}${location.search}`, {
      replace: true,
      state: { ...(location.state || {}), tradesTab: v },
    });
  };
  // Per-trade mutation state. A single global busyId disabled the whole list and
  // pushed every failure into a page-level snackbar; with six trades open, one
  // failing card must not freeze the other five.
  const [mutations, setMutations] = useState({});
  const [celebration, setCelebration] = useState(null);

  const myId = user?._id;
  // `pollView` of the list on screen, written by every successful load.
  const shown = useRef("");

  // Returns the list it just stored (or null if the refetch itself failed), so
  // a caller can reason about where a row landed without waiting for a render.
  //
  // `quiet === true` is a background reload: no loader over the list and no
  // snackbar on failure, from this page or from the global interceptor.
  // Strictly `true`, so the Try again click event is a normal load.
  const load = useCallback(async (quiet) => {
    const loud = quiet !== true;
    try {
      if (loud) setLoading(true);
      const data = await getMyTradeRequests(loud ? undefined : { quiet });
      const list = Array.isArray(data) ? data : [];
      setTrades(list);
      shown.current = pollView(list);
      setLoadError(false);
      setLoaded(true);
      return list;
    } catch (err) {
      if (loud) setSnack("error", err);
      setLoadError(true);
      return null;
    } finally {
      if (loud) setLoading(false);
    }
  }, [setSnack]);

  // Stable id, not the user object — see TradeJourneyPage for why.
  useEffect(() => {
    if (myId) load();
  }, [myId, load]);

  // Follow the badge poll, which also runs the moment the app comes back to
  // the front. The list used to load once per visit, so an answer from the
  // other trader showed only after a tab switch (B-50), and "We couldn't load
  // your trades" stayed up after the server was back while the poll was
  // already getting answers (B-61). So: reload quietly when the poll saw a
  // trade change that the list does not show yet, and retry whenever it gets
  // an answer while this page is in its error state.
  //
  // "That the list does not show yet": an action here reloads the list and
  // then asks the poll, whose answer differs from the last one but not from
  // the list. Reloading on that fetched the whole list twice per tap.
  const seenSig = useRef(tradesSig);
  useEffect(() => {
    const changed = seenSig.current !== tradesSig;
    seenSig.current = tradesSig;
    if (myId && (loadError || (changed && pollView(JSON.parse(tradesSig)) !== shown.current))) load(true);
    // Keyed on `syncedAt` alone: it moves on every answer, `tradesSig` with it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [syncedAt]);

  const handleAction = async (action, trade) => {
    // No `accept`: an offer is accepted only on its own page (Q-07).
    const fn = {
      decline: declineTradeRequest,
      cancel: cancelTradeRequest,
      abandon: abandonTradeRequest,
      dispute: disputeTradeRequest,
      complete: confirmTradeCompletion,
    }[action];
    if (!fn) return;
    const id = trade._id;
    setMutations((m) => ({ ...m, [id]: { busy: true, error: null } }));

    // Written by the catch, read by the tail below. The refetch used to be the
    // LAST statement of this `try`, so any rejection jumped straight past it and
    // the row went on offering an action the server had already refused, with
    // the tab count contradicting the nav badge 30s later (P2-55).
    let failure = null;
    try {
      const result = await fn(trade._id);

      if (action === "complete") {
        const after = result && typeof result === "object" ? { ...trade, ...result } : trade;
        // If API returns partial fields only, merge completion flags from response.
        const interpreted = interpretCompleteResult(trade, after, myId);
        setSnack("success", interpreted.snack);
        // Every confirm is a funnel step; the trade only truly completes on the
        // second (mutual) confirmation — that's when showCelebration is true.
        trackEvent("trade_completion_confirmed", { finalized: !!interpreted.showCelebration });
        if (interpreted.showCelebration) {
          // `trade_completed` is emitted server-side, from the branch that
          // actually performs the completion transition. Firing it here as well
          // double-counted the funnel's success metric.
          const imgs = tradeCardImages(after, myId);
          setCelebration({
            mineSrc: imgs.mine[0],
            theirsSrc: imgs.theirs[0],
          });
          // Only here. The FIRST confirmation transfers nothing — the server
          // moves the cards inside its `iFinalised` guard when the second side
          // confirms (tradeController.js:690) — so refreshing on every confirm
          // would be a wasted fetch against an unchanged collection. The
          // collector who confirmed first is covered elsewhere: their badge
          // poll sees the trade become completed and CollectionProvider
          // reloads then (B-56).
          // Not awaited: loadCollection swallows its own errors, and neither
          // the celebration nor the inbox refetch below should wait on a Vault
          // refetch.
          refreshCollection();
          // Same branch, same reason: the wishlist pruning happens inside the
          // same server-side transfer, so it is exactly as pointless to re-read
          // the hearts after a one-sided confirm.
          refreshWishlistIds();
        }
      } else {
        const msg = {
          decline: "Trade declined.",
          cancel: "Trade request cancelled.",
        }[action];
        setSnack("success", msg);
      }
    } catch (err) {
      // The message goes on the card itself, below (B-31: no toast as well).
      claimRefusals();
      const message = typeof err === "string" ? err : err?.message || "That didn't work";
      // `useAxios` stamps `httpStatus` onto the error it rejects with; tradeApi
      // narrows most failures to a bare message string, in which case this is
      // null and the message itself classifies the refusal.
      const status = err?.httpStatus ?? err?.response?.status ?? err?.status ?? null;
      failure = { message, stale: isStaleTradeError(message, status) };
    }

    // Rendered INSIDE this card, not as a page banner — and written BEFORE the
    // refetch, so the message survives the reload and follows the row to
    // whichever tab its real status puts it on.
    setMutations((m) => ({
      ...m,
      [id]: { busy: false, error: failure ? failure.message : null },
    }));

    // A network drop or a 5xx says nothing about the trade, so those keep the
    // old behaviour exactly: show the in-card error, leave the list untouched.
    if (failure && !failure.stale) return;

    const after = await load();
    // The nav badge polls independently, but a user who just acted on a
    // trade shouldn't see a stale count until the next 30s tick.
    refreshTradeActions();
    // Acting marked this trade's notifications seen server-side (P0 area C);
    // re-read them so the badge and the "New update" marker drop now.
    refreshNotifications();

    // The resync can move the row off the tab being looked at, and then nothing
    // on screen explains what happened: the catch above claimed the refusal for
    // the card, so useAxios raised no toast, and the card carrying the message
    // is now somewhere else.
    if (
      failure &&
      Array.isArray(after) &&
      !inboxTabs(after, myId)[tab].items.some((t) => t._id === id)
    ) {
      setSnack("error", failure.message);
    }
  };

  // The one place a journey step becomes a side effect. Nothing here may guess:
  // a step with no entry in PRIMARY_INTENT does nothing at all, because the only
  // available guess is a mutation and the wrong mutation is unrecoverable.
  const handlePrimary = (trade, action) => {
    const intent = PRIMARY_INTENT[action?.key];
    if (!intent) {
      if (process.env.NODE_ENV !== "production") {
        // Loud in dev so the next added journey step can't repeat P0-16 quietly.
        console.error(
          `[trades] Journey step "${action?.key}" has no entry in PRIMARY_INTENT; ` +
            "the inbox button will do nothing until one is added."
        );
      }
      return;
    }
    if (intent.mutate) {
      handleAction(intent.mutate, trade);
      return;
    }
    navigate(intent.to(trade));
  };

  const tabs = useMemo(() => inboxTabs(trades, myId), [trades, myId]);
  const updatedTradeIds = useMemo(
    () => tradesWithUnreadUpdate(notifications, unreadTradeIds),
    [notifications, unreadTradeIds]
  );

  // Open on the first tab with trades in it — once. A failed first load waits
  // for the retry that succeeds; every later load leaves the tab alone.
  useEffect(() => {
    if (loading || loadError || tabDecided.current) return;
    tabDecided.current = true;
    setTab(initialTabIndex(tabs));
  }, [loading, loadError, tabs]);

  if (!user) {
    return (
      <Container sx={{ py: 6, textAlign: "center" }}>
        <Typography variant="h6" gutterBottom>
          Sign in to see your trades
        </Typography>
        <Button variant="contained" onClick={() => navigate(ROUTES.LOGIN)}>
          Log In
        </Button>
      </Container>
    );
  }

  const items = tabs[tab].items;
  const otherTab = items.length === 0 ? otherTabWithTrades(tabs, tab) : null;

  return (
    <Container maxWidth="sm" sx={{ py: 3 }} data-testid="trades-page">
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
        <SwapHorizIcon color="primary" />
        {/* The page's h1 (E2E audit 7·15): same look, a heading to navigate by. */}
        <Typography variant="h5" component="h1" fontWeight={800}>
          Trades
        </Typography>
      </Box>

      <SafetyChecklist />

      <Paper variant="outlined" sx={{ borderRadius: 2, mb: 2 }}>
        <Tabs
          value={tab}
          onChange={(_, v) => chooseTab(v)}
          // All four on screen at phone width. "scrollable" cut History off at
          // the edge with no scroll arrow on mobile, so its count was unread.
          variant="fullWidth"
          sx={{
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: 600,
              minHeight: 48,
              minWidth: 0,
              px: 0.5,
              fontSize: "0.8rem",
              lineHeight: 1.2,
            },
          }}
        >
          {tabs.map((t) => (
            <Tab key={t.label} label={loaded ? `${t.label} (${t.items.length})` : t.label} />
          ))}
        </Tabs>
      </Paper>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <Loader context="panel" label="Loading your trades" />
        </Box>
      ) : loadError && trades.length === 0 ? (
        // Gated on the UNFILTERED `trades`, never on the tab-filtered `items`.
        // `load()` also runs as handleAction's post-action refetch, so a
        // failure there arrives with the user's real inbox already in state;
        // keying this arm on the flag alone — or on `items`, which a
        // legitimately empty tab makes 0 — would replace those trades with an
        // error card. With `trades` the list stays on screen and the snackbar
        // is the report, which is the pre-existing behaviour for that path.
        <Paper
          variant="outlined"
          sx={{ p: 4, textAlign: "center", borderRadius: 2 }}
          data-testid="trades-load-error"
        >
          <Typography variant="subtitle1" fontWeight={700} gutterBottom>
            We couldn&apos;t load your trades
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            Something went wrong on our end or with your connection, so we don&apos;t know what
            is in your inbox right now.
          </Typography>
          <Button variant="contained" onClick={() => load()} data-testid="trades-load-retry">
            Try again
          </Button>
        </Paper>
      ) : items.length === 0 && otherTab !== null ? (
        // This tab is empty but others are not. "No trades yet" here told a
        // collector with live trades that they had none, and sent them away to
        // the map (device run 2026-09-27: Needs you empty, Active 1, History 2).
        <Paper
          variant="outlined"
          sx={{ p: 4, textAlign: "center", borderRadius: 2 }}
          data-testid="trades-empty-tab"
        >
          <SwapHorizIcon sx={{ fontSize: 44, color: "text.disabled", mb: 1.5 }} />
          <Typography variant="subtitle1" fontWeight={700} gutterBottom>
            {EMPTY_TAB_TITLE[tab]}
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            Your other trades are in {tabs[otherTab].label}.
          </Typography>
          <Button
            variant="contained"
            onClick={() => chooseTab(otherTab)}
            data-testid="trades-empty-go-tab"
          >
            {`Go to ${tabs[otherTab].label} (${tabs[otherTab].items.length})`}
          </Button>
        </Paper>
      ) : items.length === 0 ? (
        // Trades is the HERO tab — the centred one in the bottom nav, so it is
        // the first thing a new account taps, and for a beta tester it is
        // empty for days. It said "Nothing here yet." and nothing else: no
        // icon, no explanation, no way on. Every sibling empty state in the
        // app (wishlist, collection, listings) offers a next step; this one is
        // the most prominent and offered the least.
        <Paper
          variant="outlined"
          sx={{ p: 4, textAlign: "center", borderRadius: 2 }}
          data-testid="trades-empty"
        >
          <SwapHorizIcon sx={{ fontSize: 44, color: "text.disabled", mb: 1.5 }} />
          <Typography variant="subtitle1" fontWeight={700} gutterBottom>
            No trades yet
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            Trades show up here once you or another collector makes an offer.
            Find someone holding a card you want, and start one.
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate(ROUTES.MAP)}
            data-testid="trades-empty-cta"
          >
            Find collectors
          </Button>
        </Paper>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {items.map((t) => (
            <TradeInboxCard
              key={t._id}
              trade={t}
              myId={myId}
              busy={!!mutations[t._id]?.busy}
              hasUpdate={updatedTradeIds.has(String(t._id))}
              error={mutations[t._id]?.error || null}
              primaryLabel={PRIMARY_LABEL[nextActionFor(t, myId)?.key] || null}
              onOpen={(tr) => navigate(`${ROUTES.TRADES}/${tr._id}`)}
              onPrimary={handlePrimary}
              // Decline is final and sat one gap from Accept with no confirm
              // (B-66). It now opens the trade with "Decline this offer?" over
              // it, so the confirm (in the trade page's own lazy chunk) is
              // asked with the whole offer on screen.
              onSecondary={(tr) =>
                navigate(`${ROUTES.TRADES}/${tr._id}`, { state: { decline: true } })
              }
            />
          ))}
        </Box>
      )}

      <TradeCompletionMoment
        open={!!celebration}
        onClose={() => setCelebration(null)}
        mineSrc={celebration?.mineSrc}
        theirsSrc={celebration?.theirsSrc}
      />
    </Container>
  );
};

export { TradeCard };
export default TradesPage;
